/**
 * useAssessmentSession — Assessment session management hook
 *
 * Encapsulates all assessment-related state and handlers:
 * - Blueprint generation, attempt tracking, report building
 * - Session recovery (crash resilience)
 * - Per-question timing
 *
 * Moved from src/hooks/useAssessmentSession.ts in R32 (DDD Fase 5).
 */

import { useState, useRef, useEffect } from 'react'
import {
  type Question,
  type ModuleId,
  getQuestionWithExplanation,
} from '../../content'
import type { FamilyMasteryState } from '../../mastery'
import { GroupId } from '../../config/systemConfig'
import {
  AssessmentConfig,
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentAnswer,
  AssessmentReport,
} from '../types'
import {
  generateAssessmentBlueprint,
  diagnoseTargetedMode,
} from '../utils/assessmentBlueprintGenerator'
import {
  saveBlueprint,
  saveAttempt,
  saveReport,
} from '../utils/assessmentStorage'
import { buildAssessmentReport } from '../utils/assessmentReportBuilder'

import {
  saveSessionState,
  getRecoverableSession,
  clearSessionState,
  RecoverableSession,
} from '../utils/assessmentSessionRecovery'
import { dataLayer } from '../../data/dataLayer'
// R2-BLUEPRINT-GUARD: filterSession is no longer needed here — the
// pedagogical guard now runs inside generateAssessmentBlueprint(). See
// the comment in startAssessment() for details.
import { error as logError, debug } from '../../utils/logger'

interface UseAssessmentSessionParams {
  familyMastery: Record<string, FamilyMasteryState>
  selectedGroup: GroupId
  recordAnswer: (
    module: ModuleId,
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
    timeMs?: number,
    helpUsed?: boolean,
  ) => void
  allQuestions: Question[]
  /** Called once on mount if a recoverable session is found */
  isReady: boolean
  /** The ID of the learner taking the assessment */
  learnerId?: string
}

interface AssessmentStartResult {
  questions: Question[]
  /**
   * R2-RESUME (F-AS-R1-RESUME-LOSS, May 2026): When the learner resumes a
   * recovered session, the hook returns the FULL blueprint question list
   * plus a resumeFrom payload that tells the UI where to pick up. This
   * preserves the original total-question count in the header (e.g.
   * "Vraag 13 van 30" instead of the previously misleading "Vraag 1 van 18")
   * and lets the learner navigate back to review earlier answers.
   */
  resumeFrom?: ResumeContext
}

/**
 * Outcome of `completeAssessment` (AS-2026-07-P1-03).
 *
 * `persisted: true` is the ONLY signal that the attempt and report are
 * durably stored. Downstream side-effects that assert "this assessment is
 * done" — most importantly marking a teacher assignment complete — must be
 * gated on it.
 */
export interface CompleteAssessmentResult {
  persisted: boolean
  report: AssessmentReport | null
  reason?: 'no-active-attempt' | 'storage-failure'
}

export interface ResumeContext {
  startIndex: number
  /** Map: question index → user's previously chosen original-option index. */
  initialChoices: Record<number, number>
  /** Indices of questions already answered before the resume. */
  initialAnsweredIndices: number[]
}

export function useAssessmentSession({
  familyMastery,
  selectedGroup,
  recordAnswer,
  allQuestions,
  isReady,
  learnerId,
}: UseAssessmentSessionParams) {
  // ── State ──────────────────────────────────────────────────────────
  const [assessmentBlueprint, setAssessmentBlueprint] =
    useState<AssessmentBlueprint | null>(null)
  const [assessmentAttempt, setAssessmentAttempt] =
    useState<AssessmentAttempt | null>(null)
  const latestAttemptRef = useRef<AssessmentAttempt | null>(null)
  const [assessmentStartTime, setAssessmentStartTime] = useState<number>(0)
  const [preFamilyMastery, setPreFamilyMastery] = useState<
    Record<string, FamilyMasteryState>
  >({})
  const questionStartTimeRef = useRef<number>(Date.now())
  const [assessmentReport, setAssessmentReport] =
    useState<AssessmentReport | null>(null)
  const [recoverableSession, setRecoverableSession] =
    useState<RecoverableSession | null>(null)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)
  const [assessmentQuestions, setAssessmentQuestions] = useState<Question[]>([])
  /**
   * R2-RESUME: Populated by `resumeAssessment` and consumed by the UI
   * (AssessmentSession via AppContent). Cleared when the assessment
   * completes or is abandoned.
   */
  const [resumeContext, setResumeContext] = useState<ResumeContext | null>(null)

  /**
   * R2-RACE (F-AS-R2-RECOVERY-RACE, May 2026): Mirror of
   * `assessmentBlueprint` so the async recovery check can read the LATEST
   * value (state closures inside the async would be stale).
   */
  const blueprintRef = useRef<AssessmentBlueprint | null>(null)
  useEffect(() => {
    blueprintRef.current = assessmentBlueprint
  }, [assessmentBlueprint])

  // ── Recovery check on mount / learner change ───────────────────────
  //
  // AS-2026-07-P1-02 (July 2026): the check is scoped to the CURRENT learner
  // and re-runs when the learner changes (account switch, or a parent/teacher
  // switching which child they are viewing). Any modal that was on screen for
  // the previous learner is dropped first, so a recovery prompt can never
  // carry over from one learner to the next.
  useEffect(() => {
    if (!isReady) return
    let cancelled = false
    setRecoverableSession(null)
    if (blueprintRef.current) return
    const checkRecovery = async () => {
      const session = await getRecoverableSession(learnerId)
      // R2-RACE: between firing the async call and its resolution the
      // user may have started a fresh assessment. Skip the modal in
      // that case — the new blueprint takes precedence.
      if (!cancelled && session && !blueprintRef.current) {
        setRecoverableSession(session)
      } else if (session && blueprintRef.current) {
        debug(
          '[Assessment] Discarding recoverable session: a fresh blueprint is already active',
        )
      }
    }
    checkRecovery()
    return () => {
      cancelled = true
    }
  }, [isReady, learnerId])

  // ── Handlers ───────────────────────────────────────────────────────

  const startAssessment = (
    config: AssessmentConfig,
  ): AssessmentStartResult | null => {
    // R2-RACE: clear any pending recoverable session — the user has
    // explicitly chosen to start a new assessment.
    setRecoverableSession(null)
    setResumeContext(null)

    // ── F-AS-R2-TARGETED-TIER-GUARD (May 2026) ──────────────────────
    // Targeted mode lets a teacher hand-pick families. Previously, if none
    // of the chosen families had MC questions in the learner's tier range,
    // we'd silently produce a zero-question blueprint and fall through to
    // a generic "no questions available" message. Run a pre-flight
    // diagnostic so the learner sees a precise, actionable explanation.
    const diagnosis = diagnoseTargetedMode(config, selectedGroup)
    if (diagnosis && diagnosis.eligible.length === 0) {
      const { requested, notFound, droppedForTier, droppedForNoMC } = diagnosis
      logError(
        `[Assessment] Targeted mode produced zero eligible families. ` +
          `Requested ${requested.length}; notFound=${notFound.length}, ` +
          `droppedForTier=${droppedForTier.length}, droppedForNoMC=${droppedForNoMC.length}.`,
      )
      // Prefer the most informative cause for the user-facing message.
      let message: string
      if (droppedForTier.length === requested.length) {
        message =
          'Geen vragen beschikbaar: de geselecteerde onderdelen passen niet bij het groepsniveau van deze leerling. Vraag de leerkracht om andere onderdelen te kiezen.'
      } else if (droppedForNoMC.length === requested.length) {
        message =
          'Geen toetsbare vragen beschikbaar voor de geselecteerde onderdelen (een toets gebruikt alleen meerkeuzevragen). Vraag de leerkracht om andere onderdelen te kiezen.'
      } else if (notFound.length === requested.length) {
        message =
          'De geselecteerde onderdelen zijn niet meer beschikbaar in het systeem. Vraag de leerkracht om de toets opnieuw aan te maken.'
      } else {
        message =
          'Geen vragen beschikbaar voor de geselecteerde onderdelen op dit groepsniveau. Vraag de leerkracht om andere onderdelen te kiezen.'
      }
      setAssessmentError(message)
      return null
    }
    if (diagnosis && diagnosis.eligible.length < diagnosis.requested.length) {
      debug(
        `[Assessment] Targeted mode: ${diagnosis.eligible.length}/${diagnosis.requested.length} families eligible. ` +
          `notFound=${diagnosis.notFound.length}, droppedForTier=${diagnosis.droppedForTier.length}, droppedForNoMC=${diagnosis.droppedForNoMC.length}.`,
      )
    }

    // Capture pre-assessment mastery state (deep clone).
    // ASSESSMENT_DOMAIN_RC_AUDIT §6 P3: upgraded from JSON.parse(JSON.stringify(...))
    // to structuredClone — native, faster, preserves Date/Map/Set/undefined.
    let preMastery: Record<string, FamilyMasteryState> = {}
    try {
      preMastery = structuredClone(familyMastery || {})
    } catch (err) {
      logError('Failed to clone familyMastery:', err)
    }
    setPreFamilyMastery(preMastery)

    // Generate blueprint
    const blueprint = generateAssessmentBlueprint(
      config,
      familyMastery || {},
      selectedGroup,
    )

    if (blueprint.questions.length === 0) {
      setAssessmentError(
        'Geen vragen beschikbaar voor deze configuratie. Probeer andere vakken te selecteren.',
      )
      return null
    }

    // F-AS-R1-STORAGE-QUOTA (May 2026): blueprint persistence is best-effort
    // at start time (the learner can still take the assessment from in-memory
    // state for this session), but if persistence fails the recovery layer
    // will not be able to restore the session after a refresh / crash. Log
    // and surface a soft warning so the learner knows their progress will
    // not survive a reload.
    saveBlueprint(blueprint).then((ok) => {
      if (!ok) {
        logError(
          `[Assessment] Blueprint ${blueprint.id} did not persist — session-recovery will be unavailable for this attempt.`,
        )
        setAssessmentError(
          'Let op: er is weinig opslagruimte beschikbaar. Je toets gaat door, maar als je nu de pagina ververst kan je niet hervatten. Maak indien mogelijk ruimte vrij.',
        )
      }
    })
    setAssessmentBlueprint(blueprint)

    // Create attempt
    const attempt: AssessmentAttempt = {
      id: `attempt_${Date.now()}`,
      blueprintId: blueprint.id,
      startedAt: Date.now(),
      completedAt: null,
      selectedGroup,
      answers: [],
      countsForMastery: config.countsForMastery,
      totalTimeSeconds: 0,
      status: 'in-progress',
      domainId: config.domainId,
      assignmentId: config.assignmentId,
      learnerId,
    }
    setAssessmentAttempt(attempt)
    setAssessmentStartTime(Date.now())
    questionStartTimeRef.current = Date.now()

    // Save initial session state for recovery
    saveSessionState(blueprint, attempt, Date.now(), preMastery)

    // R2-BLUEPRINT-GUARD (F-AS-R1-BLUEPRINT-GUARD-DESYNC, May 2026):
    // The pedagogical guard now runs INSIDE generateAssessmentBlueprint().
    // The blueprint is therefore guard-clean by construction — there is no
    // need to re-filter here. This eliminates the silent shortfall where
    // the runtime guard would fatal-reject items the blueprint had already
    // promised, leaving the learner with fewer questions than the count
    // displayed in the header.
    //
    // For visibility, surface the guard stats from the blueprint's
    // selectionPlan.guardStats — see types.ts.
    const finalQuestions = blueprint.questions
      .map((bq) => getQuestionWithExplanation(bq.questionId))
      .filter((q): q is Question => q !== null)

    const bpGuardStats = blueprint.selectionPlan.guardStats
    if (bpGuardStats && bpGuardStats.fatalRejected > 0) {
      debug(
        `[Assessment] Blueprint-time guard: ${bpGuardStats.fatalRejected} fatal pre-filtered, ${bpGuardStats.softFailed} soft passes`,
      )
    }

    setAssessmentQuestions(finalQuestions)
    return { questions: finalQuestions }
  }

  /**
   * F-AS-R1-STORAGE-QUOTA (May 2026): this is the data-loss-critical path.
   * If the final `saveAttempt` or `saveReport` write fails (quota exceeded,
   * eviction couldn't free enough room, etc.) and we *also* clear the
   * in-memory state, the learner sees a success UI for an assessment whose
   * record no longer exists anywhere. To prevent that:
   *
   *   1. Await BOTH writes before touching any state.
   *   2. If either write returns `false`, set a user-visible
   *      `assessmentError` and keep the in-memory attempt/blueprint/answers
   *      around so the learner (or a future retry path) can attempt to
   *      re-persist or at least screenshot the result.
   *   3. Only clear local state once both writes have succeeded.
   *
   * AS-2026-07-P1-03 (July 2026): the function now RETURNS an explicit
   * outcome. Callers must await it and may only record downstream
   * side-effects (such as marking a teacher assignment complete) when
   * `persisted === true`. Previously `App.tsx` invoked this fire-and-forget
   * and marked the assignment complete regardless, so a storage failure
   * produced a "completed" assignment with no durable attempt or report.
   */
  const completeAssessment = async (): Promise<CompleteAssessmentResult> => {
    // Read from ref to avoid stale closure — React state may not yet
    // reflect the last handleAnswer call when this runs immediately after.
    const currentAttempt = latestAttemptRef.current || assessmentAttempt
    if (!currentAttempt || !assessmentBlueprint) {
      return { persisted: false, reason: 'no-active-attempt', report: null }
    }

    const totalTimeSeconds = Math.floor(
      (Date.now() - assessmentStartTime) / 1000,
    )

    const completedAttempt: AssessmentAttempt = {
      ...currentAttempt,
      completedAt: Date.now(),
      totalTimeSeconds,
      status: 'completed',
    }

    const report = buildAssessmentReport(
      completedAttempt,
      assessmentBlueprint,
      preFamilyMastery,
      familyMastery || {},
    )

    const [attemptSaved, reportSaved] = await Promise.all([
      saveAttempt(completedAttempt),
      saveReport(report),
    ])

    if (!attemptSaved || !reportSaved) {
      logError(
        `[Assessment] completeAssessment storage failure: attempt=${attemptSaved}, report=${reportSaved}. Keeping in-memory state so the learner can retry or screenshot.`,
      )
      setAssessmentError(
        'Je toets is afgerond, maar we konden het resultaat niet opslaan (te weinig opslagruimte). Maak ruimte vrij of maak een screenshot van je antwoorden voordat je deze pagina verlaat.',
      )
      // Still surface the report in memory so the learner can see + screenshot.
      setAssessmentReport(report)
      return { persisted: false, reason: 'storage-failure', report }
    }

    setAssessmentReport(report)
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    setAssessmentQuestions([])
    latestAttemptRef.current = null
    setResumeContext(null)
    clearSessionState()
    return { persisted: true, report }
  }

  const handleAnswer = (
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
  ): void => {
    if (!assessmentAttempt || !assessmentBlueprint) return

    const question = allQuestions.find((q) => q.id === questionId)
    if (!question) return

    const blueprintQuestion = assessmentBlueprint.questions.find(
      (bq) => bq.questionId === questionId,
    )
    if (!blueprintQuestion) return

    // Calculate time spent using per-question timer
    const now = Date.now()
    const timeSpent = Math.floor((now - questionStartTimeRef.current) / 1000)
    questionStartTimeRef.current = now

    const answer: AssessmentAnswer = {
      questionId,
      userChoice,
      isCorrect,
      timeSpent: Math.max(1, timeSpent),
      familyId: blueprintQuestion.familyId,
      module: blueprintQuestion.module,
    }

    // Replace existing answer if user went back and changed it, otherwise append
    const existingAnswerIndex = assessmentAttempt.answers.findIndex(
      (a) => a.questionId === questionId,
    )
    let newAnswers = [...assessmentAttempt.answers]

    if (existingAnswerIndex >= 0) {
      // Add the new time spent to the previous time spent
      answer.timeSpent += newAnswers[existingAnswerIndex].timeSpent
      newAnswers[existingAnswerIndex] = answer
    } else {
      newAnswers.push(answer)
    }

    const updatedAttempt = {
      ...assessmentAttempt,
      answers: newAnswers,
    }
    setAssessmentAttempt(updatedAttempt)
    latestAttemptRef.current = updatedAttempt

    // Persist for crash recovery
    saveSessionState(
      assessmentBlueprint,
      updatedAttempt,
      assessmentStartTime,
      preFamilyMastery,
    )

    // Optionally update mastery
    if (assessmentAttempt.countsForMastery) {
      recordAnswer(
        question.module,
        questionId,
        isCorrect,
        userChoice,
        payload,
        errorType,
        delta,
      )
    }
  }

  const resumeAssessment = (): AssessmentStartResult | null => {
    if (!recoverableSession) return null

    const {
      blueprint,
      attempt,
      startTime,
      preFamilyMastery: preMastery,
    } = recoverableSession

    setAssessmentBlueprint(blueprint)
    setAssessmentAttempt(attempt)
    latestAttemptRef.current = attempt
    setAssessmentStartTime(startTime)
    setPreFamilyMastery(preMastery)

    // R2-RESUME (F-AS-R1-RESUME-LOSS): build the FULL question list and
    // compute the resume context so the UI shows the real total count
    // and lets the learner navigate back through already-answered items.
    const allQuestionsForBlueprint = blueprint.questions
      .map((bq) => getQuestionWithExplanation(bq.questionId))
      .filter((q): q is Question => q !== null)

    if (allQuestionsForBlueprint.length === 0) {
      clearSessionState()
      setRecoverableSession(null)
      return null
    }

    // Build a questionId → blueprint index map (the blueprint may have
    // dropped some IDs that no longer resolve).
    const idToIndex = new Map<string, number>()
    allQuestionsForBlueprint.forEach((q, i) => idToIndex.set(q.id, i))

    const initialChoices: Record<number, number> = {}
    const initialAnsweredIndices: number[] = []
    for (const answer of attempt.answers) {
      const idx = idToIndex.get(answer.questionId)
      if (idx === undefined) continue
      initialChoices[idx] = answer.userChoice
      initialAnsweredIndices.push(idx)
    }

    // First unanswered index = where the learner picks up. Falls back
    // to the last index if every question happens to be answered (the
    // session was effectively complete but never finalised).
    const answeredSet = new Set(initialAnsweredIndices)
    let startIndex = 0
    for (let i = 0; i < allQuestionsForBlueprint.length; i++) {
      if (!answeredSet.has(i)) {
        startIndex = i
        break
      }
      startIndex = i // keep advancing if every index is answered
    }

    const resumeFrom: ResumeContext = {
      startIndex,
      initialChoices,
      initialAnsweredIndices,
    }
    setResumeContext(resumeFrom)
    setRecoverableSession(null)
    setAssessmentQuestions(allQuestionsForBlueprint)

    // Reset the per-question timer so the time spent on the resumed
    // question doesn't include the time the modal was on screen.
    questionStartTimeRef.current = Date.now()

    return { questions: allQuestionsForBlueprint, resumeFrom }
  }

  /**
   * F-AS-R1-ASSIGNED-ASSESSMENT-EXIT (May 2026): When the abandoned
   * attempt belongs to a teacher assignment, mark the assignment as
   * abandoned by this learner so the teacher dashboard can surface
   * "started and gave up" as distinct from "not yet started". Fire-and-
   * forget — the abandon flow already proceeds optimistically and a
   * storage failure here should not block the user-facing exit.
   */
  const notifyAssignmentAbandoned = (attempt: AssessmentAttempt): void => {
    const aid = attempt.assignmentId
    const sid = attempt.learnerId || learnerId
    if (!aid || !sid) return
    dataLayer
      .markAssignmentAbandonedByStudent(aid, sid)
      .catch((e) =>
        logError(
          '[Assessment] Failed to mark assignment abandoned by student',
          e,
        ),
      )
  }

  const abandonRecovery = (): void => {
    if (recoverableSession) {
      const abandonedAttempt: AssessmentAttempt = {
        ...recoverableSession.attempt,
        completedAt: Date.now(),
        totalTimeSeconds: Math.floor(
          (Date.now() - recoverableSession.startTime) / 1000,
        ),
        status: 'abandoned',
      }
      saveAttempt(abandonedAttempt)
      notifyAssignmentAbandoned(abandonedAttempt)
    }
    clearSessionState()
    setRecoverableSession(null)
  }

  const abandonCurrentAssessment = (): void => {
    if (assessmentAttempt) {
      const abandonedAttempt: AssessmentAttempt = {
        ...assessmentAttempt,
        completedAt: Date.now(),
        totalTimeSeconds: Math.floor((Date.now() - assessmentStartTime) / 1000),
        status: 'abandoned',
      }
      saveAttempt(abandonedAttempt)
      notifyAssignmentAbandoned(abandonedAttempt)
      clearSessionState()
    }
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    setAssessmentQuestions([])
    setResumeContext(null)
  }

  const clearAssessmentState = (): void => {
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    setAssessmentReport(null)
    setAssessmentError(null)
    setAssessmentQuestions([])
    setResumeContext(null)
  }

  /**
   * R2-L1 (Assessment audit 2026): Reset the per-question timer when the
   * UI navigates to a different question (Next without answering, Previous,
   * etc.). Without this, `timeSpent` on the next handleAnswer would include
   * all the time the learner spent reviewing earlier questions, inflating
   * the per-question metric.
   */
  const resetQuestionTimer = (): void => {
    questionStartTimeRef.current = Date.now()
  }

  return {
    // State
    assessmentBlueprint,
    assessmentAttempt,
    assessmentReport,
    assessmentError,
    recoverableSession,
    assessmentStartTime,
    assessmentQuestions,
    resumeContext,

    // Handlers
    startAssessment,
    completeAssessment,
    handleAnswer,
    resumeAssessment,
    abandonRecovery,
    abandonCurrentAssessment,
    clearAssessmentState,
    resetQuestionTimer,

    // Setters (for modals)
    setAssessmentError,
    setAssessmentReport,
  }
}
